/**
 * Contract Collaterals Section Component
 * Manages multiple collateral providers per contract.
 *
 * Each row has its own `secured` flag. Secured rows require type,
 * multiplier and nominal amount; unsecured rows only require provider
 * and amount. The detail emphasis is on secured rows.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { formatNumber } from '../../utils/validation';
import ConfirmDialog from '../common/ConfirmDialog';
import StakeholderAutocomplete from '../common/StakeholderAutocomplete';

const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

function CurrencyInput({ value, onChange, label, helperText, disabled, sx, error, ...props }) {
  const [focused, setFocused] = useState(false);
  const showFormatted = !focused || disabled;
  const rawValue = (() => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : String(num);
  })();

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : rawValue}
      onChange={disabled ? undefined : (e) => onChange(e.target.value.replace(/\s/g, ''))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type={showFormatted ? 'text' : 'number'}
      inputProps={showFormatted ? {} : { step: 0.01, min: 0 }}
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      disabled={disabled}
      error={error}
      sx={sx}
    />
  );
}

const EMPTY_FORM = {
  collateral_provider_id: '',
  secured: true,
  currency_id: '',
  collateral_amount: '',
  collateral_type_id: '',
  collateral_multiplier: '',
  nominal_amount: '',
  collateral_in_favour_of: '',
  collateral_description: '',
  contingent_liability: '',
};

// Fixed Actions width so the edit/delete icons anchor at the same right-edge
// position as the Broker / Stamp Duty tables.
const ACTIONS_COL_W = 96;
// Fixed widths on the three numeric columns so Cover Value + Multiplier sit
// tight against Nominal / Face Value (grouped on the right next to Actions);
// the left text columns absorb the slack.
const COVER_COL_W = 160;
const MULT_COL_W = 100;
const NOMINAL_COL_W = 160;

function ContractCollateralsSection({
  collaterals = [],
  availableProviders = [],
  collateralTypes = [],
  currencies = [],
  defaultCurrencyId = null,
  onAddCollateral,
  onUpdateCollateral,
  onRemoveCollateral,
  onProviderCreated,
  disabled = false,
  currencyCode = 'ZAR',
  countries = [],
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });

  const handleOpenDialog = (row = null) => {
    if (row) {
      setEditingRow(row);
      setFormData({
        collateral_provider_id: row.collateral_provider_id,
        secured: row.secured !== false,
        currency_id: row.currency_id ?? defaultCurrencyId ?? '',
        collateral_amount: row.collateral_amount ?? '',
        collateral_type_id: row.collateral_type_id ?? '',
        collateral_multiplier: row.collateral_multiplier ?? '',
        nominal_amount: row.nominal_amount ?? '',
        collateral_in_favour_of: row.collateral_in_favour_of ?? '',
        collateral_description: row.collateral_description ?? '',
        contingent_liability: row.contingent_liability ?? '',
      });
    } else {
      setEditingRow(null);
      setFormData({
        ...EMPTY_FORM,
        currency_id: defaultCurrencyId ?? '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRow(null);
    setFormData({ ...EMPTY_FORM });
  };

  const isSubmitDisabled = () => {
    if (!formData.collateral_provider_id) return true;
    const amount = parseFloat(formData.collateral_amount);
    if (!isFinite(amount) || amount <= 0) return true;
    if (formData.secured) {
      if (!formData.collateral_type_id) return true;
      const mult = parseFloat(formData.collateral_multiplier);
      if (!isFinite(mult)) return true;
      const nom = parseFloat(formData.nominal_amount);
      if (!isFinite(nom)) return true;
    }
    return false;
  };

  const handleSubmit = () => {
    if (isSubmitDisabled()) return;

    const provider = availableProviders.find(p => p.id === parseInt(formData.collateral_provider_id, 10));
    const type = collateralTypes.find(t => t.id === parseInt(formData.collateral_type_id, 10));
    const selectedCurrency = currencies.find(c => c.id === parseInt(formData.currency_id, 10));

    const payload = {
      collateral_provider_id: parseInt(formData.collateral_provider_id, 10),
      collateral_provider_name: provider?.name || editingRow?.collateral_provider_name || 'Unknown',
      secured: !!formData.secured,
      currency_id: formData.currency_id ? parseInt(formData.currency_id, 10) : null,
      currency_code: selectedCurrency?.currency_code || editingRow?.currency_code || null,
      currency_symbol: selectedCurrency?.currency_symbol || editingRow?.currency_symbol || null,
      collateral_amount: parseFloat(formData.collateral_amount),
      collateral_type_id: formData.secured && formData.collateral_type_id ? parseInt(formData.collateral_type_id, 10) : null,
      collateral_type: formData.secured ? (type?.type || editingRow?.collateral_type || null) : null,
      collateral_multiplier: formData.secured && formData.collateral_multiplier !== '' ? parseFloat(formData.collateral_multiplier) : null,
      nominal_amount: formData.secured && formData.nominal_amount !== '' ? parseFloat(formData.nominal_amount) : null,
      collateral_in_favour_of: formData.secured ? (formData.collateral_in_favour_of || null) : null,
      collateral_description: formData.secured ? (formData.collateral_description || null) : null,
      contingent_liability: formData.secured && formData.contingent_liability !== '' ? parseFloat(formData.contingent_liability) : null,
    };

    if (editingRow) {
      onUpdateCollateral(editingRow.id, payload);
    } else {
      onAddCollateral(payload);
    }
    handleCloseDialog();
  };

  const handleRemove = (row) => {
    setConfirmDialog({
      open: true,
      onConfirm: () => {
        onRemoveCollateral(row);
        setConfirmDialog({ open: false, onConfirm: null });
      },
    });
  };

  // Helpers to drive the Cover / Multiplier / Nominal triangle. The user
  // edits any one of the three; whichever they edit, recompute the other
  // dependent one so Multiplier = Cover ÷ Nominal always holds.
  //
  // Rules:
  //  - Edit Cover  → if Multiplier present, recompute Nominal; else if Nominal present, recompute Multiplier.
  //  - Edit Multiplier → if Cover present, recompute Nominal; else if Nominal present, recompute Cover.
  //  - Edit Nominal → if Cover present, recompute Multiplier; else if Multiplier present, recompute Cover.
  // Clearing a field is allowed and does not trigger recomputation.
  const handleCoverChange = (val) => {
    setFormData(prev => {
      const next = { ...prev, collateral_amount: val };
      const cover = parseFloat(val);
      if (val === '' || !isFinite(cover) || cover <= 0) return next;
      const mult = parseFloat(prev.collateral_multiplier);
      const nominal = parseFloat(prev.nominal_amount);
      if (prev.collateral_multiplier !== '' && isFinite(mult) && mult > 0) {
        next.nominal_amount = (cover / mult).toFixed(2);
      } else if (prev.nominal_amount !== '' && isFinite(nominal) && nominal > 0) {
        next.collateral_multiplier = (cover / nominal).toFixed(4);
      }
      return next;
    });
  };

  const handleMultiplierChange = (val) => {
    setFormData(prev => {
      const next = { ...prev, collateral_multiplier: val };
      const mult = parseFloat(val);
      if (val === '' || !isFinite(mult) || mult <= 0) return next;
      const cover = parseFloat(prev.collateral_amount);
      const nominal = parseFloat(prev.nominal_amount);
      if (prev.collateral_amount !== '' && isFinite(cover) && cover > 0) {
        next.nominal_amount = (cover / mult).toFixed(2);
      } else if (prev.nominal_amount !== '' && isFinite(nominal) && nominal > 0) {
        next.collateral_amount = (nominal * mult).toFixed(2);
      }
      return next;
    });
  };

  const handleNominalChange = (val) => {
    setFormData(prev => {
      const next = { ...prev, nominal_amount: val };
      const nominal = parseFloat(val);
      if (val === '' || !isFinite(nominal) || nominal <= 0) return next;
      const cover = parseFloat(prev.collateral_amount);
      const mult = parseFloat(prev.collateral_multiplier);
      if (prev.collateral_amount !== '' && isFinite(cover) && cover > 0) {
        next.collateral_multiplier = (cover / nominal).toFixed(4);
      } else if (prev.collateral_multiplier !== '' && isFinite(mult) && mult > 0) {
        next.collateral_amount = (nominal * mult).toFixed(2);
      }
      return next;
    });
  };

  // Filter out already-added providers (unless editing the row that owns it)
  const getAvailableProvidersForSelect = () => {
    const addedIds = collaterals.map(c => c.collateral_provider_id);
    if (editingRow) return availableProviders;
    return availableProviders.filter(p => !addedIds.includes(p.id));
  };

  const renderDetailCell = (row, formatted) => (
    row.secured === false
      ? <Typography variant="body2" color="text.secondary">—</Typography>
      : <Typography variant="body2">{formatted}</Typography>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">
              Collateral Providers ({collaterals.length})
            </Typography>
          </Box>
          {!disabled && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Collateral
            </Button>
          )}
        </Box>

        {collaterals.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
            <Typography color="textSecondary">
              No collateral added yet. Click "Add Collateral" to record a provider.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Provider</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>In Favour Of</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: COVER_COL_W }}>Cover Value</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: MULT_COL_W }}>Multiplier</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: NOMINAL_COL_W }}>Nominal / Face Value</TableCell>
                  {!disabled && <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {collaterals.map((row) => (
                  <TableRow key={row.id ?? `new-${row.collateral_provider_id}`}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="medium">
                          {row.collateral_provider_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.secured === false ? 'Unsecured' : 'Secured'}
                        color={row.secured === false ? 'default' : 'success'}
                        size="small"
                        variant={row.secured === false ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>
                      {renderDetailCell(row, row.collateral_type || '—')}
                    </TableCell>
                    <TableCell>
                      {renderDetailCell(row, row.collateral_in_favour_of || '—')}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: COVER_COL_W }}>
                      <Typography variant="body2">
                        {(row.currency_code || currencyCode)} {formatNumber(row.collateral_amount, 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ width: MULT_COL_W }}>
                      {renderDetailCell(row, row.collateral_multiplier != null ? parseFloat(row.collateral_multiplier).toFixed(4) : '—')}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: NOMINAL_COL_W }}>
                      {renderDetailCell(row, row.nominal_amount != null ? `${(row.currency_code || currencyCode)} ${formatNumber(row.nominal_amount, 2)}` : '—')}
                    </TableCell>
                    {!disabled && (
                      <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>
                        <Tooltip title="Edit collateral">
                          <IconButton size="small" onClick={() => handleOpenDialog(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove collateral">
                          <IconButton size="small" color="error" onClick={() => handleRemove(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
          <DialogTitle>
            {editingRow ? 'Edit Collateral' : 'Add Collateral'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={12}>
                <StakeholderAutocomplete
                  stakeholderType="collateral-provider"
                  options={getAvailableProvidersForSelect()}
                  value={formData.collateral_provider_id}
                  onChange={(id) => setFormData(prev => ({ ...prev, collateral_provider_id: id }))}
                  onStakeholderCreated={(newProvider) => {
                    if (onProviderCreated) onProviderCreated(newProvider);
                    setFormData(prev => ({ ...prev, collateral_provider_id: newProvider.id }));
                  }}
                  label="Collateral Provider *"
                  disabled={!!editingRow}
                  countries={countries}
                />
              </Grid>

              {/* Row 2: Currency + Type */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  label="Currency *"
                  fullWidth
                  value={formData.currency_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency_id: e.target.value }))}
                  helperText="Defaults to the contract currency; change if the collateral is denominated differently"
                >
                  <MenuItem value=""><em>Select currency…</em></MenuItem>
                  {currencies
                    .filter(c => c.currency_code && c.active !== false)
                    .map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.currency_code}</MenuItem>
                    ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  label="Collateral Type *"
                  fullWidth
                  value={formData.collateral_type_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, collateral_type_id: e.target.value }))}
                >
                  <MenuItem value=""><em>Select type…</em></MenuItem>
                  {collateralTypes.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Row 3: the three linked numeric values on a single line */}
              <Grid size={{ xs: 12, md: 4 }}>
                <CurrencyInput
                  label="Cover Value *"
                  value={formData.collateral_amount}
                  onChange={handleCoverChange}
                  helperText="Secured value of this collateral"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Collateral Multiplier *"
                  type="number"
                  fullWidth
                  value={formData.collateral_multiplier}
                  onChange={(e) => handleMultiplierChange(e.target.value)}
                  inputProps={{ step: 0.0001, min: 0 }}
                  helperText="Ratio (e.g. 1.25 = 125%). Last-edited field recomputes the dependent one."
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <CurrencyInput
                  label="Nominal / Face Value *"
                  value={formData.nominal_amount}
                  onChange={handleNominalChange}
                  helperText="Face value of the underlying instrument"
                />
              </Grid>

              {/* Row 4: In Favour Of */}
              <Grid size={12}>
                <TextField
                  label="In Favour Of"
                  fullWidth
                  value={formData.collateral_in_favour_of}
                  onChange={(e) => setFormData(prev => ({ ...prev, collateral_in_favour_of: e.target.value }))}
                  helperText="Beneficiary of the collateral"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.collateral_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, collateral_description: e.target.value }))}
                  helperText="Additional detail about the collateral"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled()}
            >
              {editingRow ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Remove Collateral"
        message="Are you sure you want to remove this collateral provider?"
        confirmText="Remove"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, onConfirm: null })}
      />
    </Box>
  );
}

export default ContractCollateralsSection;
